<template>
    <div class="auth-page fill-height">
        <div class="auth-center">
            <div class="auth-card">

                <!-- Heading -->
                <div class="auth-heading mb-8">
                    <h1 class="auth-title">
                        {{ isConsultationFlow ? 'Book a Consultation' : 'Create your account' }}
                    </h1>
                    <p class="auth-subtitle mt-1">
                        <template v-if="isConsultationFlow">
                            Sign up to book a session with one of our supervisors.
                        </template>
                        <template v-else>
                            Get started free — no credit card required.
                        </template>
                    </p>
                </div>

                <!-- Register Form Component -->
                <register-form
                    :loading="loading"
                    :email-error.sync="emailError"
                    :flow="flow"
                    @register="registerAction"
                />

                <p class="sign-in-prompt text-center mt-6">
                    Already have an account?
                    <router-link :to="{name: 'login'}" class="sign-in-link">Sign in</router-link>
                </p>

            </div>
        </div>
        <slot name="footer" />
    </div>
</template>

<script>

// @ is an alias to /src
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

import RegisterForm from '@/components/custom/register-form'

export default {
    name: 'register',
    props: {
        flow: { type: String, default: 'evaluation' }, // 'evaluation' | 'consultation'
    },
    data(){
        return {
            loading: false,
            emailError: '',
        };
    },
    components: {RegisterForm},
    computed:{
        ...GlobalComputed,
        isConsultationFlow(){
            return this.flow === 'consultation';
        },
    },
    methods: {
        registerAction(data){
            this.closeMsg();
            this.loading = true;

            this.$store.dispatch('registerAction', data).then((resolveData)=>{
                this.loading = false;
                if (resolveData.redirect){
                    this.$router.push({name: 'confirm'});
                }else{
                    // No OTP — go directly to the appropriate booking step.
                    // this.user is already set in Vuex by registerAction.
                    const u = this.user;
                    if (u && u.needs_consultation_redirect) {
                        this.$router.push({name: 'consultation-booking'});
                    } else if (u && u.needs_evaluation_redirect) {
                        this.$router.push({name: 'evaluation-booking'});
                    } else {
                        this.$router.push({name: 'home'});
                    }
                }
            }).catch(error=>{
                this.loading = false;
                if (error.response && error.response.status === 422){
                    // coming errors from backend
                    let errors = error.response.data.errors;
                    let errorMsg = errors[Object.keys(errors)[0]][0];
                    if (errorMsg === 'The email has already been taken.') {
                        this.emailError = 'This email is already registered. Please sign in instead.';
                        this.showMsg('This email is already registered. Please sign in instead.');
                    } else if (errors['email']) {
                        this.emailError = errorMsg;
                        this.showMsg(errorMsg);
                    } else {
                        this.showMsg(errorMsg);
                    }
                }else{
                    this.showMsg('Error occurred during creating your account');
                }
            })
        },

        showMsg(text, color = 'error', timeout = 4000){
            this.$store.dispatch('addSnackMsg', {show: true, text: text, color: color, timeout: timeout, msgBar: true});
        },

        ...GlobalMethods,

    },
    watch: {
        fetchedUserCountry(country){
            if (country === 'EG'){
                this.$router.push({name: 'login-teachers'});
            }
        },
    },
    mounted(){
        if (this.fetchedUserCountry === 'EG'){
            this.$router.push({name: 'login-teachers'});
        }
    }
}
</script>

<style lang="scss" scoped>
.auth-page {
    background: #F5F5F5;
}

.auth-center {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
}

.auth-card {
    width: 100%;
    max-width: 520px;
    background: #FFFFFF;
    border-radius: 16px;
    border: 1px solid #EBEBEB;
    padding: 40px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    @media (max-width: 480px) {
        padding: 32px 24px;
        border-radius: 12px;
    }
}

/* ── Heading ── */
.auth-title {
    font-family: 'Inter', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #1E1E1E;
    letter-spacing: -0.4px;
    line-height: 1.2;
}

.auth-subtitle {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #8A8A8A;
    margin: 0;
}

/* ── Override register-form fields ── */
::v-deep .v-input__slot {
    border-radius: 10px !important;
    background: #FAFAFA !important;
    min-height: 46px !important;
}

::v-deep .v-text-field__slot input,
::v-deep .v-select__selection {
    font-family: 'Inter', sans-serif;
    font-size: 14.5px;
    color: #1E1E1E;
}

::v-deep .v-text-field__slot input::placeholder {
    color: #C4C4C4;
}

::v-deep .v-input--is-focused:not(.error--text) fieldset {
    border-color: #D4780A !important;
    border-width: 1.5px !important;
}

::v-deep .v-label {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #9E9E9E;
}

::v-deep .v-input--is-focused .v-label {
    color: #D4780A;
}

/* ── Sign in prompt ── */
.sign-in-prompt {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #9E9E9E;
    margin: 0;
}

.sign-in-link {
    color: #D4780A;
    font-weight: 600;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}
</style>

<style lang="scss">
.vue-tel-input-vuetify{
    .country-code{
        margin-right: 6px;
    }
}
</style>
